import { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate } from "react-router-dom";

import DashboardLayout from "@/components/layout/DashboardLayout";
import { useCurrentUser } from "@/hooks/useCurrentUser";

import { ChatContainer } from "../components/ChatContainer";
import { ThreadsSidebar } from "../components/ThreadsSidebar";
import { useMobileChatHistory } from "../context/MobileChatHistoryContext";
import { useThreads } from "../hooks/useThreads";

interface LocationState {
  initialPrompt?: string;
}

const CoPilot = () => {
  const { user } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState | null;
  const { user: currentUser } = useCurrentUser();
  const [pendingPrompt, setPendingPrompt] = useState<string | undefined>(
    undefined,
  );
  const [promptKey, setPromptKey] = useState<string | null>(null);

  // Mobile chat history context
  const mobileChatHistory = useMobileChatHistory();

  // Threads management
  const {
    threads,
    activeThreadId,
    isLoading: threadsLoading,
    createNewThread,
    startNewThread,
    selectThread,
    deleteThread,
    loadThreads,
  } = useThreads();

  // Start a new thread (locally only - thread created on first message)
  const handleNewThread = useCallback(() => {
    startNewThread();
    setPendingPrompt(undefined); // Clear any pending prompt
    console.log("Started new thread (will be created on first message)");
  }, [startNewThread]);

  const handleThreadSelect = useCallback((threadId: string) => {
    selectThread(threadId);
    console.log("Selected thread:", threadId);
  }, [selectThread]);

  const handleThreadDelete = useCallback(async (threadId: string) => {
    try {
      await deleteThread(threadId);
      console.log("Deleted thread:", threadId);
    } catch (error) {
      console.error("Failed to delete thread:", error);
    }
  }, [deleteThread]);

  const handleCreateThread = async (): Promise<string> => {
    const newThread = await createNewThread();
    return newThread.thread_id;
  };

  const handleThreadCreated = (threadId: string) => {
    // Make sure the newly created thread is selected
    selectThread(threadId);
  };

  const handleMessageSent = () => {
    // Refresh threads list to get updated title
    loadThreads();
  };

  // Register chat data with mobile context
  useEffect(() => {
    mobileChatHistory.registerChatData({
      threads,
      activeThreadId,
      isLoading: threadsLoading,
      onThreadSelect: handleThreadSelect,
      onThreadDelete: handleThreadDelete,
      onNewThread: handleNewThread,
    });

    // Unregister when unmounting
    return () => {
      mobileChatHistory.unregisterChatData();
    };
     
  }, [threads, activeThreadId, threadsLoading, handleThreadSelect, handleThreadDelete, handleNewThread]);

  // Get user's first name for the greeting
  const userName =
    currentUser?.firstName?.split(" ")[0] ||
    user?.name?.split(" ")[0] ||
    "there";

  // Get the active thread's title
  const activeThread = threads.find((t) => t.thread_id === activeThreadId);
  const threadTitle = activeThread?.title;

  // Handle initialPrompt from navigation (e.g., from Agents Library)
  useEffect(() => {
    const initialPrompt = locationState?.initialPrompt;

    // Only process if we have a prompt
    if (!initialPrompt) {
      return;
    }

    // Create a unique key for this prompt to prevent re-processing
    const currentPromptKey = `${initialPrompt}-${location.key}`;
    if (promptKey === currentPromptKey) {
      return;
    }

    // Mark this prompt as handled
    setPromptKey(currentPromptKey);

    // Always start a new thread when coming from agents library
    // Thread will be created on the server when first message is sent
    startNewThread();
    console.log("Started new thread for agent prompt (will be created on first message)");

    // Set the pending prompt to be passed to ChatContainer
    setPendingPrompt(initialPrompt);

    // Clear the navigation state to prevent re-processing on refresh
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    locationState?.initialPrompt,
    location.key,
    promptKey,
    startNewThread,
    navigate,
    location.pathname,
  ]);

  return (
    <DashboardLayout containerClassName="!p-0">
      <div className="flex h-[calc(100dvh-4rem)] md:h-[100dvh]">
        {/* Desktop Threads Sidebar - hidden on mobile (handled by MobileChatHistory in NavBar) */}
        <div className="hidden md:block">
          <ThreadsSidebar
            threads={threads}
            activeThreadId={activeThreadId}
            onThreadSelect={handleThreadSelect}
            onThreadDelete={handleThreadDelete}
            onNewThread={handleNewThread}
            isLoading={threadsLoading}
          />
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <ChatContainer
            userName={userName}
            threadId={activeThreadId}
            threadTitle={threadTitle}
            isOnboarded={currentUser?.onboarded || false}
            onCreateThread={handleCreateThread}
            onThreadCreated={handleThreadCreated}
            onMessageSent={handleMessageSent}
            initialPrompt={pendingPrompt}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CoPilot;
